import { Link } from 'react-router-dom';
import {
  Construction,
  Database,
  Gauge,
  CalendarClock,
  ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export default function Maintenance() {
  return (
    <div className="flex h-full flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Maintenance</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Predictive-risk dashboard for fleet maintenance — flags vehicles drifting
          above their L/100km baseline, due for service, or showing odometer-rollback
          signals.
        </p>
      </div>

      <Card className="border-amber-200 bg-amber-50/40">
        <CardHeader className="flex flex-row items-center gap-2 pb-2">
          <Construction className="h-4 w-4 text-amber-600" />
          <CardTitle className="text-sm">Awaiting data feed</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          A maintenance-events feed (workshop visits, parts replaced, service intervals)
          has not yet been wired in — there is no schema for it on the Postgres side, and
          the ML model that scores vehicles for predictive-risk is still pending. Until
          both land, the partial signals listed below are the closest equivalent.
        </CardContent>
      </Card>

      <div>
        <h2 className="text-base font-semibold">Available today</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Partial signals you can use right now while the dedicated dashboard is wired up.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center gap-2 pb-2">
            <Gauge className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm">Consumption-drift alerts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              Vehicles drifting above their L/100km baseline already surface as
              efficiency-rule alerts in the Fraud Center.
            </p>
            <Link
              to="/fraud?category=efficiency"
              className="inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline"
            >
              Open Fraud Center filtered by category_code=efficiency
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-2 pb-2">
            <Database className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm">Odometer-rollback flags</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              Transactions tagged with{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                data_quality_flags ? 'odometer_review_needed'
              </code>{' '}
              expose suspect odometer readings before a workshop ever sees them.
            </p>
            <Link
              to="/data-quality"
              className="inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline"
            >
              Open Data Quality
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-2 pb-2">
            <CalendarClock className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm">Vehicle aging signal</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              Service-due cohorts can be inferred from{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                fleet.vehicle_asset.manufacture_year
              </code>{' '}
              until a real service-interval feed is connected.
            </p>
            <a
              href="/api/v1/docs"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline"
            >
              Browse fleet endpoints in the API docs
              <ExternalLink className="h-3 w-3" />
            </a>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-2 pb-2">
            <Construction className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm">Rule families covering this space</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              The{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">EFF_*</code>{' '}
              rule family is the maintenance-adjacent slice of fraud detection — drift,
              outlier consumption, and abnormal fill cadence.
            </p>
            <Link
              to="/fraud?rule_id=EFF_"
              className="inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline"
            >
              Open Fraud Center filtered by rule_id like EFF_*
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
